// Shared display helpers + small UI bits for the purchasing & intake feature
// (plan.md G20). Money/quantity MATH of record always comes from `@zebra/core`
// (payable.ts, moisture.ts) via `ledgerPreview.ts` — everything here is
// presentation-only (formatting an already-computed value for a human).
import { isCountLine, type PricingBasis, type VatTreatment } from '@zebra/core';

/** ZAR cents (bigint or its wire decimal-string form) -> "R1 234,56" (en-ZA:
 * space-thousands, comma-decimal — SA convention, not US). Mirrors services/
 * api's `formatCents` (modules/intake/lib.ts) but lives here separately since
 * that server helper isn't exported to the browser bundle. Grouping goes
 * through `Intl.NumberFormat('en-ZA').format(rand)` on the bigint whole-rand
 * value directly (its `.format` overload accepts `bigint`, no `Number(cents)`
 * conversion), so precision is exact even for amounts beyond
 * Number.MAX_SAFE_INTEGER; the cents remainder is likewise derived from the
 * bigint's own `% 100n`, never floating point. */
export function formatMoneyCents(cents: bigint | string | number): string {
  const value = typeof cents === 'bigint' ? cents : BigInt(Math.trunc(Number(cents)));
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const rand = abs / 100n;
  const remainder = (abs % 100n).toString().padStart(2, '0');
  const grouped = new Intl.NumberFormat('en-ZA').format(rand);
  return `${negative ? '-' : ''}R${grouped},${remainder}`;
}

/** A decimal-string quantity (kg, tonnes, %) -> a grouped display string with a
 * fixed number of decimals, e.g. "12,340.500". Display-only; never round-trips
 * back into a request body (those always resend the original string/number). */
export function formatQtyDisplay(value: string | number | null | undefined, decimals = 3): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('en-ZA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** User-typed Rand amount (e.g. "1234.56") -> integer ZAR cents as a decimal
 * string, for wire bodies that use `moneyCentsSchema` (bigint-or-numeric-string).
 * Deterministic string/BigInt math (never `Math.round(x * 100)`) so entering a
 * price never drifts off by a cent to floating-point error. */
export function randToCents(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '0';
  const negative = trimmed.startsWith('-');
  const abs = negative ? trimmed.slice(1) : trimmed;
  const [wholePart, fracPart = ''] = abs.split('.');
  const frac = `${fracPart}00`.slice(0, 2);
  const digits = `${wholePart || '0'}${frac}`.replace(/^0+(?=\d)/, '');
  const cents = BigInt(digits || '0');
  return (negative ? -cents : cents).toString();
}

/** Inverse of `randToCents` — integer ZAR cents (wire decimal string) -> a plain
 * Rand string for a form input, e.g. "1234.56". */
export function centsToRand(cents: string | null | undefined): string {
  if (cents == null || cents === '') return '';
  const value = BigInt(cents);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const rand = abs / 100n;
  const remainder = (abs % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${rand.toString()}.${remainder}`;
}

function toMilliKg(value: string): bigint {
  const trimmed = value.trim();
  if (!trimmed) return 0n;
  const negative = trimmed.startsWith('-');
  const abs = negative ? trimmed.slice(1) : trimmed;
  const [whole, frac = ''] = abs.split('.');
  const paddedFrac = `${frac}000`.slice(0, 3);
  const milli = BigInt(`${whole || '0'}${paddedFrac}`);
  return negative ? -milli : milli;
}

function fromMilliKg(value: bigint): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const whole = abs / 1000n;
  const frac = (abs % 1000n).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${frac}`;
}

/** Exact decimal-string subtraction for weighbridge kg readings (gross - tare),
 * scaled through integer milli-kg so the wizard's live preview never drifts off
 * a gram to floating-point error before the server computes the authoritative
 * `netKg` itself (services/api's `computeNetKg`, decimal.js-backed). */
export function subtractKg(grossKg: string, tareKg: string): string {
  return fromMilliKg(toMilliKg(grossKg) - toMilliKg(tareKg));
}

/** VAT on a payable amount (BUILD_SPEC.md §7: "STANDARD 15% | ZERO_RATED |
 * EXEMPT"), round-half-up to whole cents — mirrors services/api's
 * `modules/intake/lib.ts` `vatCentsFor` exactly (same formula, same rounding
 * rule), so this preview always agrees with what `/goods-receipts/:id/post`
 * will actually compute and persist. */
export function vatCentsForDisplay(payableCents: bigint, vatTreatment: VatTreatment): bigint {
  if (vatTreatment !== 'STANDARD') return 0n;
  return (payableCents * 15n + 50n) / 100n;
}

export const PRICING_BASIS_LABELS: Record<PricingBasis, string> = {
  FLAT_WET: 'Flat (wet tonnes)',
  BDT: 'BDT (dry tonnes)',
  MOISTURE_BAND: 'Moisture band',
};

/** One-line, first-time explanation of what each pricing basis means for the
 * payable amount — the label alone (e.g. "BDT (dry tonnes)") doesn't say why a
 * wetter load is worth less (guidance wave, 2026-08-25). */
export const PRICING_BASIS_DESCRIPTIONS: Record<PricingBasis, string> = {
  FLAT_WET: 'One price per wet tonne delivered — moisture content does not affect what is payable.',
  BDT: 'Priced per bone-dry tonne: the wet weight is reduced by its moisture content before pricing, so a wetter load is worth less.',
  MOISTURE_BAND: 'Price depends on which moisture-content band the load falls into — wetter bands price lower.',
};

/** A PO line's mass/count-mode fields — `PurchaseOrderLineRow` (api.ts) and every
 * other shape carrying the same {qtyTonnes, pricingBasis, qty, priceCentsPerUnit}
 * quartet (exactly one pair populated per `isCountLine`/`isMassLine`'s doc). */
export interface PoLineQtyBasis {
  qtyTonnes: string | null;
  pricingBasis: PricingBasis | null;
  qty: string | null;
  priceCentsPerUnit: string | null;
}

/** Mode-aware one-line description of a PO line's quantity + pricing basis —
 * "2,000 units @ R8.50/unit" for an EACH (count) line, "12.500 t (Flat (wet
 * tonnes))" for a mass line. Discriminates with `isCountLine` (THE shared
 * mass/count discriminant, `@zebra/core`) rather than a local null check, so
 * this never drifts from the server's own definition (fix round G-WebA,
 * 2026-07-07 — the previous unconditional `${line.qtyTonnes} t
 * (${PRICING_BASIS_LABELS[line.pricingBasis]})` template rendered "undefined t
 * (undefined)" for an EACH line, since both fields are null there). */
export function formatPoLineQtyBasis(line: PoLineQtyBasis): string {
  if (isCountLine(line)) {
    return `${formatQtyDisplay(line.qty, 0)} units @ R${centsToRand(line.priceCentsPerUnit)}/unit`;
  }
  return `${formatQtyDisplay(line.qtyTonnes)} t (${PRICING_BASIS_LABELS[line.pricingBasis ?? 'FLAT_WET']})`;
}

/** Server response types (`TicketResponse.weighedAt`, `GoodsReceiptResponse.
 * receivedAt`, etc) are typed `Date` — the zod schema's `z.coerce.date()`
 * OUTPUT type — but the browser only ever sees the raw `JSON.parse`'d wire
 * value (a plain ISO string; nothing here is actually re-parsed through zod
 * client-side). `asDate` bridges the two so callers can safely construct a
 * `Date` regardless of which the runtime value really is. */
export function asDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/** A response's Date-typed-but-really-string field -> a `datetime-local`
 * input value ("YYYY-MM-DDTHH:mm"), for pre-filling an edit form (e.g. the
 * goods-receipt draft modal). */
export function toDatetimeLocalValue(value: Date | string): string {
  const d = asDate(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` input default: "now", truncated to the minute, in the
 * shape the native input expects (`YYYY-MM-DDTHH:mm`). Native inputs are used
 * for weighedAt/receivedAt throughout this feature (§11 gatehouse-tablet
 * usability — a native picker is faster to drive on a touchscreen than a
 * custom calendar popover, and needs no extra CSS bundle). */
export function nowForDatetimeLocal(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
